<template>
  <div :class="`bg-${activeTab.theme} position-relative vh-100`">
    <b-row class="slider" ref="container">
      <b-col cols="12" md="3" class="p-5 text-white">
        <h2 v-text="activeTab.caption" />
        <p class="text-justify" v-html="activeTab.description" />
      </b-col>
      <b-col cols="12" md="9" class="py-2">
        <div class="slider__box d-flex">
          <div
            v-if="activeTab.key !== 'tab1'"
            class="slider__box__item slider__box__item__shadow bg-white p-3 rounded mx-2 cursor_pointer h-100"
          >
            <b-overlay :show="loading" rounded="sm" class="h-100">
              <b-row class="slider__box__content">
                <b-col cols="12">
                  <BarChart
                    :labels="labels"
                    :series="series"
                    :titleText="activeTab.firstTabTitle"
                  />
                </b-col>
              </b-row>
            </b-overlay>
          </div>
          <div
            v-for="(item, index) in items"
            :key="index"
            @click.stop="setExpanded()"
            class="slider__box__item slider__box__item__shadow bg-white p-3 rounded mx-2 cursor_pointer"
            :class="{ 'slider__box__item--expanded': expanded }"
          >
            <b-overlay :show="loading" rounded="sm">
              <b-row class="slider__box__content">
                <b-col cols="12" class="text-right mb-3">
                  <span
                    v-if="expanded"
                    @click.stop="setExpanded(false)"
                    class="slider__box__item__close"
                  >
                    <mdicon name="close" size="30" />
                  </span>
                </b-col>
                <b-col cols="12">
                  <component
                    :is="`${activeTab.componentName}`"
                    :item="item"
                    :expanded="expanded"
                  >
                    <template #user-box>
                      <b-row class="border-top mt-2 pt-3 w-100">
                        <b-col cols="auto">
                          <b-img-lazy
                            :src="generateOfStorage(['image'], item)"
                            :onerror="`this.src='${$config.images.tinyLogo}'`"
                            width="48"
                            class="rounded bg-light"
                          />
                        </b-col>
                        <b-col cols="" class="pr-0">
                          <span class="text-dark">
                            {{ generateOfStorage(["fullName"], item) }}
                          </span>
                          <span class="text-muted">
                            | {{ generateOfStorage(["parvandehNumber"], item) }}
                          </span>
                          <div class="text-muted">
                            {{ generateOfStorage(["termCaption"], item) }}
                          </div>
                        </b-col>
                      </b-row>
                    </template>
                  </component>
                </b-col>
              </b-row>
            </b-overlay>
          </div>
          <div
            class="slider__box__item"
            :class="{ 'slider__box__item--expanded': expanded }"
          />
        </div>
      </b-col>
    </b-row>
    <div class="navigation" v-if="items.length">
      <div
        @click="handleNavigation('right')"
        class="navigation__item navigation__item--tight"
      >
        <mdicon name="arrow-right" size="50" />
      </div>
      <div
        @click="handleNavigation('left')"
        class="navigation__item navigation__item--left"
      >
        <mdicon name="arrow-left" size="50" />
      </div>
    </div>
  </div>
</template>

<script>
import { Bar as BarChart } from "@/components/charts";
import tab1CardContent from "./tab1CardContent.vue";
import tab2CardContent from "./tab2CardContent.vue";
import tab3CardContent from "./tab3CardContent.vue";
import { pathOr } from "ramda";
import { computed, ref, toRefs, watch } from "vue";

export default {
  components: { tab1CardContent, tab2CardContent, tab3CardContent, BarChart },
  props: {
    activeTab: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    // generate props data
    const { activeTab, loading, items } = toRefs(props);

    // handle expanded
    const expanded = ref(false);
    const setExpanded = (value = true) => {
      expanded.value = value;
    };
    watch(items, () => setExpanded(false));

    const generateOfStorage = (key, item) => {
      const storageData = localStorage.getItem("dashboard-parvandeh-codes");
      const storageItems = JSON.parse(storageData) || [];
      const currentParvandehNumber = pathOr(null, ["parvandeh_number"], item);
      const storageItem = storageItems.find(
        (row) => row.parvandehNumber === currentParvandehNumber
      );
      const output = pathOr("", key, storageItem) || "...";
      return output;
    };

    // handle slider navigation with reference of container tag
    const container = ref(null);
    const handleNavigation = (direction) => {
      // const container = this.$refs.content;
      const countNavigate = container.value.scrollLeft / window.innerWidth;
      if (direction === "left") {
        container.value.scrollLeft -= 800 + countNavigate * 50;
      } else {
        container.value.scrollLeft += 800 + countNavigate * 50;
      }
    };

    const labels = computed(() => {
      const output = [];
      for (const item of items.value) {
        output.push(generateOfStorage(["fullName"], item));
      }
      return output;
    });
    const series = computed(() => {
      // generate data of tabs bigger than tab 2
      let data = items.value.map((item) => {
        const insideItems = pathOr([], ["parent", "items"], item);
        return pathOr(0, ["0", "rank_percent"], insideItems);
      });
      // generate data of tab 2
      if (activeTab.value.key === "tab2") {
        data = items.value.map((item) =>
          pathOr(0, ["overall", "efficiency"], item)
        );
      }
      return [
        {
          showInLegend: false,
          data,
          name: "بازده انتظاری",
        },
      ];
    });

    // return data to be used in template
    return {
      activeTab,
      items,
      loading,
      expanded,
      container,
      labels,
      series,
      setExpanded,
      generateOfStorage,
      handleNavigation,
    };
  },
};
</script>

<style lang="scss" scoped>
.bg-green {
  background-color: #5eb48b !important;
}
.bg-blue {
  background-color: #86b8c9 !important;
}
.bg-orange {
  background-color: #f6be60 !important;
}
.bg-red {
  background-color: #d95650 !important;
}
.bg-purple {
  background-color: #665980 !important;
}
.footer__box {
  border-top: 1px solid white;
  bottom: 19vh;
}
.slider {
  overflow-x: scroll;
  scroll-behavior: smooth;
  &::-webkit-scrollbar {
    display: none; /* for Chrome, Safari, and Opera */
  }
  height: 80%;

  .slider__box {
    height: calc(100% - 90px);
    &__item {
      transition: all 1s ease-in-out;
      min-width: 306px;
      height: 100%;
      position: relative;
      &--expanded {
        min-width: 933px;
        // &:first-child,
        &:last-child {
          min-width: 306px;
        }
      }
      &__close {
        cursor: pointer;
        color: #626b69;
      }
      &__shadow {
        box-shadow: 0 0.5em 1em 0 #00000033;
      }
    }
  }
}
.body-box {
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -ms-flex-direction: column;
  flex-direction: column;
  justify-content: space-between;
  height: 45vh;
}
.navigation {
  &__item {
    position: absolute;
    top: 0;
    width: 70px;
    height: calc(100% - 90px);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background-color: rgba(51, 51, 51, 0.5);
    color: white;
    opacity: 0;
    transition: opacity 0.5s ease-in-out;
    &:hover {
      opacity: 1;
    }
    &--right {
      right: 0;
    }
    &--left {
      left: 0;
    }
  }
}
</style>
